import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import {ClickAwayListener, IconButton, Paper, Popper} from "@mui/material";
import React, {useCallback, useEffect, useId, useRef, useState} from "react";
import {surveyControls, surveySolidColors} from "./surveyRunnerColors";
import SurveyRunnerMarkdown from "./SurveyRunnerMarkdown";

interface Props {
    /** The help text in the guest's language: markdown with variables, like the description. */
    help: string
    /** The survey-language translator of the title the button sits in. */
    t: (key: string) => string
    /** Where the panel goes in the DOM: right after the title's heading, so its text is no part of the heading
     *  and comes next in reading order. In place while there is none. */
    panelContainer?: HTMLElement | null
}

const GLYPH_SIZE = 18;

/** The visible circle, so the hover tint and the focus ring stay close to the glyph. */
const BUTTON_SIZE = 28;

/** The touch target around it: an invisible ::after, so the title line keeps its height. */
const TOUCH_TARGET = 44;

const PANEL_MAX_WIDTH = 320;

const PANEL_SHADOW = '0 4px 16px rgba(15, 23, 42, 0.08)';

// Hoisted: MUI compares `modifiers` by identity, and an inline array rebuilds the popper on every render.
const PANEL_MODIFIERS = [
    {name: 'offset', options: {offset: [0, 8]}},
    {name: 'preventOverflow', options: {padding: 8}},
];

/**
 * The (?) after a question title. A tap or click opens the question's help below it; a second tap, a tap
 * outside, Escape or another page closes it. The glyph is one node for the button's whole life: a node
 * swapped under a finger loses the tap on phones (runner/questions/StarsQuestion.test.tsx).
 */
function SurveyRunnerHelpButton({help, t, panelContainer}: Props) {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const panelId = useId();

    // Back to the button, unless the guest has already moved on to another control.
    const closeFromKeyboard = useCallback(() => {
        const active = document.activeElement;
        const returnFocus = !active || active === document.body || !!panelRef.current?.contains(active);

        setOpen(false);
        if (returnFocus) buttonRef.current?.focus();
    }, []);

    // Escape closes the panel wherever focus is: a tap does not focus the button in every browser.
    useEffect(() => {
        if (!open) return undefined;

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeFromKeyboard();
        };

        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [open, closeFromKeyboard]);

    // From the button or the panel, Escape ends here: a dialog around the preview must not close with it.
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!open || event.key !== 'Escape') return;

        event.stopPropagation();
        closeFromKeyboard();
    };

    return (
        <>
            <IconButton
                ref={buttonRef}
                aria-label={t('More about this question')}
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={() => setOpen(current => !current)}
                onKeyDown={handleKeyDown}
                sx={{
                    width: BUTTON_SIZE,
                    height: BUTTON_SIZE,
                    p: 0,
                    // Out of the line box, so the title keeps its line height; 3px after the asterisk.
                    my: `${(GLYPH_SIZE - BUTTON_SIZE) / 2}px`,
                    ml: '-2px',
                    mr: `${(GLYPH_SIZE - BUTTON_SIZE) / 2}px`,
                    verticalAlign: 'middle',
                    color: 'text.secondary',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: `${(BUTTON_SIZE - TOUCH_TARGET) / 2}px`,
                    },
                }}
            >
                <HelpOutlineOutlinedIcon sx={{fontSize: GLYPH_SIZE}}/>
            </IconButton>

            <Popper
                open={open}
                anchorEl={buttonRef.current}
                container={panelContainer}
                disablePortal={!panelContainer}
                placement={'bottom-start'}
                modifiers={PANEL_MODIFIERS}
                // A panel the guest asked for, not a tooltip.
                role={undefined}
                sx={{zIndex: theme => theme.zIndex.tooltip}}
            >
                <ClickAwayListener
                    onClickAway={event => {
                        // The button toggles on its own click.
                        if (buttonRef.current?.contains(event.target as Node)) return;
                        setOpen(false);
                    }}
                >
                    <Paper
                        ref={panelRef}
                        id={panelId}
                        elevation={0}
                        onKeyDown={handleKeyDown}
                        sx={theme => ({
                            maxWidth: `min(${PANEL_MAX_WIDTH}px, calc(100vw - 32px))`,
                            px: 1.5,
                            py: 1.25,
                            bgcolor: surveySolidColors(theme).surface,
                            color: 'text.primary',
                            border: `1px solid ${surveyControls(theme).border}`,
                            borderRadius: 1,
                            boxShadow: PANEL_SHADOW,
                            // Body text, also when it renders in place inside the title's heading.
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: 1.5,
                            whiteSpace: 'normal',
                            textAlign: 'start',
                        })}
                    >
                        <SurveyRunnerMarkdown markdown={help} fontSize={14}/>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}

export default SurveyRunnerHelpButton;
